import React, { useCallback, useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import * as styles from './dashboard.styles';
import type DashboardOffice from './models/dashboard-office';
import { useAllDashboardOffices } from '../offices/hooks/offices.hook';
import ActivityIndicator from '../../core/components/activity-indicator/activity-indicator.component';
import BackButton from '../../core/components/back-button/back-button.component';
import { Center } from '../../core/components/center/center.component';
import Column from '../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../core/components/flex/flex.component';
import { IsMobileContext } from '../../core/context/is-mobile';
import RouteConstants from '../../core/routes.constants';
import StyleConstants from '../../core/styles.constants';
import DashboardOfficeCard from './cards/dashboard-office-card.component';
import Grid from '../../core/components/grid/grid.component';
import CommonStyles from '../../core/common.styles';
import Row from '../../core/components/row/row.component';
import * as OfficeStyles from '../offices/offices.styles';

const OfficeDashboard = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [amountOfPages, setAmountOfPages] = useState(1);
  const isMobile = useContext(IsMobileContext);
  const pageSize = isMobile ? 3 : 10;
  const officesState = useAllDashboardOffices(pageNumber, pageSize);
  const [isLoading, setIsLoading] = useState(true);
  const [offices, setOffices] = useState<DashboardOffice[] | undefined>(undefined);
  const sortedOffices = offices?.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)) ?? [];

  const navigateToOverview = useCallback((): void => {
    navigate(`/${RouteConstants.PATH_DASHBOARD}`);
  }, [navigate]);

  const didDeleteOffice = useCallback(
    (deletedOffice: DashboardOffice): void => {
      setOffices(offices?.filter((x) => x.id !== deletedOffice.id));
    },
    [offices],
  );

  if (isLoading && officesState.dashboardOffices != null) {
    setIsLoading(false);
    setOffices(officesState.dashboardOffices);
    setAmountOfPages(Math.ceil(officesState.dashboardOffices[0].totalRecords / pageSize));
  }

  useEffect(() => {
    if (officesState.dashboardOffices != null) {
      setIsLoading(false);
      setOffices(officesState.dashboardOffices);
      setAmountOfPages(Math.ceil(officesState.dashboardOffices[0].totalRecords / pageSize));
    }
  }, [officesState.dashboardOffices]);

  const showNextPage = (): void => {
    if (pageNumber >= amountOfPages) {
      setPageNumber(amountOfPages);
    } else {
      setPageNumber(pageNumber + 1);
    }
    setIsLoading(true);
  };

  const showPreviousPage = (): void => {
    if (pageNumber <= 1) {
      setPageNumber(1);
    } else {
      setPageNumber(pageNumber - 1);
    }
    setIsLoading(true);
  };

  return (
    <div className={style(CommonStyles.pageMargin)}>
      <div className={style(styles.cornerButton(isMobile))}>
        <BackButton title={t('dashboard')} navigate={navigateToOverview} />
      </div>
      <Column className={style(CommonStyles.bodyMargin(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <div className={style(CommonStyles.sectionCard(isMobile))}>
          <Column>
            <span className={style(styles.columnHeader(isMobile))}>{t('registered-offices')}</span>
            {((): React.JSX.Element => {
              if (isLoading) {
                return (
                  <Center>
                    <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
                  </Center>
                );
              }

              if (sortedOffices.length === 0) {
                <span className={style(styles.emptyListMessage)}>{t('no-offices-available')}</span>;
              }

              return (
                <Grid
                  rowClass={style(styles.gridRow)}
                  numberOfColumns={1}
                  numberOfItems={sortedOffices.length}
                  renderItem={(index): React.JSX.Element => {
                    const office = sortedOffices[index];
                    return (
                      office && (
                        <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={office.id}>
                          <DashboardOfficeCard dashboardOffice={office} handleDidDeleteOffice={didDeleteOffice} />
                        </div>
                      )
                    );
                  }}
                />
              );
            })()}
            {amountOfPages !== 0 && (
              <Row mainAxisAlignment={MainAxisAlignment.SpaceBetween} crossAxisAlignment={CrossAxisAlignment.Center}>
                <ChevronLeftRounded className={style(OfficeStyles.tooltipIcon)} onClick={showPreviousPage} />
                <div>
                  <span className={style(styles.columnHeader(isMobile))}>
                    {t('page')} {pageNumber}/{amountOfPages}
                  </span>
                </div>
                <ChevronRightRounded className={style(OfficeStyles.tooltipIcon)} onClick={showNextPage} />
              </Row>
            )}
          </Column>
        </div>
      </Column>
    </div>
  );
};

export default OfficeDashboard;
